import { Injectable, Injector, NgZone, OnDestroy, inject } from '@angular/core';
import {
  ActionSheetController,
  AlertController,
  ModalController,
  ModalOptions,
  PickerController,
  PopoverController,
} from '@ionic/angular';
import { PendingNativeTraversal } from '@state-routing/services/state-history-guard';

type StateOverlayElement = HTMLIonActionSheetElement
  | HTMLIonAlertElement
  | HTMLIonModalElement
  | HTMLIonPickerElement
  | HTMLIonPopoverElement;

interface StateManagedOverlayEntry {
  readonly element: StateOverlayElement;
  readonly id: string;
  readonly settled: Promise<void>;
  resolveSettled: () => void;
}

@Injectable()
export class StateOverlayService implements OnDestroy {
  private readonly injector = inject(Injector);
  private readonly ngZone = inject(NgZone);

  private static readonly HISTORY_STATE_KEY: string = 'stateOverlayIds';
  private static readonly HISTORY_TRAVERSAL_TIMEOUT_MS: number = 2_000;
  private static readonly WILL_PRESENT_EVENTS: readonly string[] = [
    'ionActionSheetWillPresent',
    'ionAlertWillPresent',
    'ionModalWillPresent',
    'ionPickerWillPresent',
    'ionPopoverWillPresent',
  ];

  private readonly managedEntries: StateManagedOverlayEntry[] = [];
  private readonly managedEntriesByElement = new Map<StateOverlayElement, StateManagedOverlayEntry>();
  private readonly browserBackDismissingEntries = new Set<StateManagedOverlayEntry>();
  private readonly dismissingEntries = new Set<StateManagedOverlayEntry>();
  private readonly historyBackOperations = new Set<() => void>();
  private readonly retiredOverlayIds = new Set<string>();
  private pendingNativeTraversal: PendingNativeTraversal | null = null;
  private forwardRejectionOperation: Promise<void> | null = null;
  private sequence: number = 0;
  private started: boolean = false;

  private readonly handleOverlayWillPresent = (event: Event): void => {
    if (this.isStateOverlayElement(event.target)) {
      this.register(event.target);
    }
  };

  ngOnDestroy(): void {
    if (typeof document !== 'undefined') {
      StateOverlayService.WILL_PRESENT_EVENTS.forEach((eventName: string): void => {
        document.removeEventListener(eventName, this.handleOverlayWillPresent, true);
      });
    }
    this.completeHistoryBackOperations();
    this.managedEntriesByElement.clear();
    this.dismissingEntries.clear();
    this.retiredOverlayIds.clear();
    this.started = false;
  }

  public start(): void {
    if (this.started || typeof window === 'undefined') return;

    this.started = true;
    if (typeof document !== 'undefined') {
      StateOverlayService.WILL_PRESENT_EVENTS.forEach((eventName: string): void => {
        document.addEventListener(eventName, this.handleOverlayWillPresent, true);
      });
    }
    this.removeStaleHistoryState();
    void this.registerPresentedOverlays();
  }

  public async openModal(options: ModalOptions): Promise<HTMLIonModalElement> {
    const modal: HTMLIonModalElement = await this.injector.get(ModalController).create(options);
    await modal.present();
    this.register(modal);
    return modal;
  }

  public register(element: StateOverlayElement): void {
    if (this.managedEntriesByElement.has(element)) return;

    let resolveSettled: () => void = (): void => undefined;
    const settled: Promise<void> = new Promise<void>((resolve): void => {
      resolveSettled = resolve;
    });
    const entry: StateManagedOverlayEntry = {
      element,
      id: `state-overlay-${++this.sequence}`,
      settled,
      resolveSettled,
    };
    this.managedEntries.push(entry);
    this.managedEntriesByElement.set(element, entry);
    this.pushHistoryEntry();
    void element.onWillDismiss().then((): void => {
      this.dismissingEntries.add(entry);
    });
    void element.onDidDismiss().then((): Promise<void> => this.finalizeDismiss(entry));
  }

  public async waitUntilSettled(element: StateOverlayElement): Promise<void> {
    const entry: StateManagedOverlayEntry | undefined = this.managedEntriesByElement.get(element);
    if (entry) {
      await entry.settled;
    }
  }

  public interceptBrowserPopState(event: PopStateEvent): boolean {
    const pendingNativeTraversal: PendingNativeTraversal | null = this.pendingNativeTraversal;
    if (pendingNativeTraversal) {
      this.ngZone.run((): void => {
        pendingNativeTraversal.complete(pendingNativeTraversal.isExpectedState());
      });
      return true;
    }

    const activeIds: readonly string[] = this.readHistoryIds(event.state);
    if (activeIds.some((id: string): boolean => this.retiredOverlayIds.has(id))) {
      this.ngZone.run((): void => {
        void this.rejectRetiredOverlayForward();
      });
      return true;
    }

    const topEntry: StateManagedOverlayEntry | undefined = this.resolveTopManagedEntry();
    if (!topEntry || activeIds.includes(topEntry.id)) return false;

    this.ngZone.run((): void => {
      this.browserBackDismissingEntries.add(topEntry);
      void this.dismissFromBrowserBack(topEntry);
    });
    return true;
  }

  public async closeTop(): Promise<boolean> {
    const managedEntry: StateManagedOverlayEntry | undefined = this.resolveTopManagedEntry();
    const unmanagedOverlay: StateOverlayElement | null = await this.resolveTopUnmanagedOverlay();
    if (
      unmanagedOverlay
      && (
        !managedEntry
        || this.resolveZIndex(unmanagedOverlay) >= this.resolveZIndex(managedEntry.element)
      )
    ) {
      return await unmanagedOverlay.dismiss(undefined, 'state-back');
    }

    if (managedEntry) {
      if (this.dismissingEntries.has(managedEntry)) {
        await managedEntry.settled;
        return true;
      }

      const dismissed: boolean = await managedEntry.element.dismiss(undefined, 'state-back');
      if (!dismissed) {
        if (!this.dismissingEntries.has(managedEntry)) return false;

        await managedEntry.settled;
        return true;
      }

      await managedEntry.settled;
      return true;
    }
    const dismissingEntry: StateManagedOverlayEntry | undefined = this.managedEntries[this.managedEntries.length - 1];
    if (dismissingEntry) {
      await dismissingEntry.settled;
      return true;
    }
    return false;
  }

  public async closeAll(): Promise<boolean> {
    while (await this.hasOpenOverlay()) {
      if (!await this.closeTop()) return false;
    }
    return true;
  }

  private async hasOpenOverlay(): Promise<boolean> {
    return this.managedEntries.length > 0 || await this.resolveTopUnmanagedOverlay() !== null;
  }

  private async finalizeDismiss(entry: StateManagedOverlayEntry): Promise<void> {
    this.browserBackDismissingEntries.delete(entry);
    this.dismissingEntries.delete(entry);
    const index: number = this.managedEntries.indexOf(entry);
    if (index >= 0) {
      this.managedEntries.splice(index, 1);
    }

    if (this.readCurrentHistoryIds().includes(entry.id)) {
      await this.consumeHistoryEntry(entry.id);
    }
    this.retiredOverlayIds.add(entry.id);
    entry.resolveSettled();
    this.managedEntriesByElement.delete(entry.element);
  }

  private pushHistoryEntry(): void {
    if (typeof history === 'undefined' || typeof window === 'undefined') return;

    this.retiredOverlayIds.clear();
    const state: Record<string, unknown> = this.copyHistoryState(history.state);
    state[StateOverlayService.HISTORY_STATE_KEY] = this.managedEntries
      .filter((entry: StateManagedOverlayEntry): boolean => !this.browserBackDismissingEntries.has(entry))
      .map(
        (entry: StateManagedOverlayEntry): string => entry.id,
      );
    history.pushState(state, '', window.location.href);
  }

  private async dismissFromBrowserBack(entry: StateManagedOverlayEntry): Promise<void> {
    const dismissed: boolean = await entry.element
      .dismiss(undefined, 'browser-back')
      .catch((): boolean => false);
    if (dismissed || this.dismissingEntries.has(entry)) {
      await entry.settled;
      return;
    }

    this.browserBackDismissingEntries.delete(entry);
    if (this.managedEntries.includes(entry)) {
      this.pushHistoryEntry();
    }
  }

  private async consumeHistoryEntry(overlayId: string): Promise<void> {
    const consumed: boolean = await this.waitForNativeHistoryBack(
      (): boolean => !this.readCurrentHistoryIds().includes(overlayId),
    );
    if (consumed) return;

    console.warn('[StateOverlay] browser history did not consume a dismissed overlay entry', {overlayId});
    this.removeHistoryIds(new Set<string>([overlayId]));
  }

  private async rejectRetiredOverlayForward(): Promise<void> {
    if (this.forwardRejectionOperation) {
      await this.forwardRejectionOperation;
      return;
    }

    const operation: Promise<void> = this.performRetiredOverlayForwardRejection();
    this.forwardRejectionOperation = operation;
    try {
      await operation;
    } finally {
      if (this.forwardRejectionOperation === operation) {
        this.forwardRejectionOperation = null;
      }
    }
  }

  private async performRetiredOverlayForwardRejection(): Promise<void> {
    const isExpectedState = (): boolean => !this.readCurrentHistoryIds().some(
      (id: string): boolean => this.retiredOverlayIds.has(id),
    );
    const rejected: boolean = await this.waitForNativeHistoryBack(isExpectedState);
    if (rejected) return;

    console.warn('[StateOverlay] failed to reject browser Forward into a dismissed overlay');
    this.removeHistoryIds(this.retiredOverlayIds);
  }

  private waitForNativeHistoryBack(isExpectedState: () => boolean): Promise<boolean> {
    if (typeof history === 'undefined' || this.pendingNativeTraversal) return Promise.resolve(false);

    return new Promise<boolean>((resolve): void => {
      let timeoutId: ReturnType<typeof setTimeout> | null = null;
      let completed: boolean = false;
      const abort = (): void => complete(false);
      const complete = (result: boolean): void => {
        if (completed) return;

        completed = true;
        if (timeoutId !== null) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        if (this.pendingNativeTraversal?.complete === complete) {
          this.pendingNativeTraversal = null;
        }
        this.historyBackOperations.delete(abort);
        resolve(result);
      };
      this.pendingNativeTraversal = {complete, isExpectedState};
      this.historyBackOperations.add(abort);
      timeoutId = setTimeout(abort, StateOverlayService.HISTORY_TRAVERSAL_TIMEOUT_MS);
      try {
        history.back();
      } catch {
        abort();
      }
    });
  }

  private completeHistoryBackOperations(): void {
    [...this.historyBackOperations].forEach((complete: () => void): void => complete());
  }

  private removeStaleHistoryState(): void {
    if (typeof history === 'undefined' || typeof window === 'undefined') return;
    const staleIds: readonly string[] = this.readCurrentHistoryIds();
    if (!staleIds.length) return;

    staleIds.forEach((id: string): void => {
      this.retiredOverlayIds.add(id);
    });
    this.removeHistoryIds(new Set<string>(staleIds));
  }

  private removeHistoryIds(idsToRemove: ReadonlySet<string>): void {
    if (typeof history === 'undefined' || typeof window === 'undefined') return;

    const state: Record<string, unknown> = this.copyHistoryState(history.state);
    const remainingIds: readonly string[] = this.readHistoryIds(state).filter(
      (id: string): boolean => !idsToRemove.has(id),
    );
    if (remainingIds.length) {
      state[StateOverlayService.HISTORY_STATE_KEY] = remainingIds;
    } else {
      delete state[StateOverlayService.HISTORY_STATE_KEY];
    }
    history.replaceState(state, '', window.location.href);
  }

  private readCurrentHistoryIds(): readonly string[] {
    return typeof history === 'undefined' ? [] : this.readHistoryIds(history.state);
  }

  private readHistoryIds(state: unknown): readonly string[] {
    if (!this.isRecord(state)) return [];

    const ids: unknown = state[StateOverlayService.HISTORY_STATE_KEY];
    return Array.isArray(ids)
      ? ids.filter((id: unknown): id is string => typeof id === 'string')
      : [];
  }

  private copyHistoryState(state: unknown): Record<string, unknown> {
    return this.isRecord(state) ? {...state} : {};
  }

  private resolveTopManagedEntry(): StateManagedOverlayEntry | undefined {
    for (let index: number = this.managedEntries.length - 1; index >= 0; index--) {
      const entry: StateManagedOverlayEntry = this.managedEntries[index];
      if (!this.browserBackDismissingEntries.has(entry)) return entry;
    }
    return undefined;
  }

  private async resolveTopUnmanagedOverlay(): Promise<StateOverlayElement | null> {
    const overlays: StateOverlayElement[] = await this.resolveUnmanagedOverlays();
    if (!overlays.length) return null;

    return overlays.reduce((top: StateOverlayElement, candidate: StateOverlayElement): StateOverlayElement => (
      this.resolveZIndex(candidate) > this.resolveZIndex(top) ? candidate : top
    ));
  }

  private async registerPresentedOverlays(): Promise<void> {
    const overlays: StateOverlayElement[] = await this.resolveUnmanagedOverlays();
    overlays
      .sort((first: StateOverlayElement, second: StateOverlayElement): number => (
        this.resolveZIndex(first) - this.resolveZIndex(second)
      ))
      .forEach((element: StateOverlayElement): void => this.register(element));
  }

  private async resolveUnmanagedOverlays(): Promise<StateOverlayElement[]> {
    const overlays: StateOverlayElement[] = [];
    const actionSheet: HTMLIonActionSheetElement | undefined = await this.injector.get(ActionSheetController).getTop();
    const alert: HTMLIonAlertElement | undefined = await this.injector.get(AlertController).getTop();
    const modal: HTMLIonModalElement | undefined = await this.injector.get(ModalController).getTop();
    const picker: HTMLIonPickerElement | undefined = await this.injector.get(PickerController).getTop();
    const popover: HTMLIonPopoverElement | undefined = await this.injector.get(PopoverController).getTop();
    [actionSheet, alert, modal, picker, popover].forEach((element: StateOverlayElement | undefined): void => {
      if (element && !this.managedEntries.some(
        (entry: StateManagedOverlayEntry): boolean => entry.element === element,
      )) {
        overlays.push(element);
      }
    });
    return overlays;
  }

  private resolveZIndex(element: StateOverlayElement): number {
    if (typeof window === 'undefined') return 0;
    const zIndex: number = Number(window.getComputedStyle(element).zIndex);
    return Number.isFinite(zIndex) ? zIndex : 0;
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
  }

  private isStateOverlayElement(value: EventTarget | null): value is StateOverlayElement {
    return value instanceof HTMLElement
      && (
        value.tagName === 'ION-ACTION-SHEET'
        || value.tagName === 'ION-ALERT'
        || value.tagName === 'ION-MODAL'
        || value.tagName === 'ION-PICKER'
        || value.tagName === 'ION-POPOVER'
      );
  }
}
